import { useState, useEffect } from "react";
import type { PluginDetailProps, MarketplaceEntry } from "../types/index";
import styles from "./PluginDetailPage.module.css";
import InstallInstructions from "../components/InstallInstructions";
import InstallInClaude from "../components/InstallInClaude";
import DesktopDownload from "../components/DesktopDownload";
import { CLIENT_CONFIG } from "../components/ClientBadge";
import { OfficialBadge } from "../components/OfficialBadge";

import { deleteSkill, getPresignedDownloadUrl, GitHubProxyError, legacyInstallKey } from "../lib/github-proxy-client";
import { isAuthenticated } from "../lib/auth";
import { env } from "../lib/env";

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export default function PluginDetailPage({ plugin, breadcrumbs = [], parentPlugin, onBackToCatalog, onBreadcrumbClick, onNavigateToPlugin, findPlugin, user, onEdit, onDelete, onClientClick, onCategoryClick, onKeywordClick, onHelp }: PluginDetailProps): JSX.Element {
  // installKey routes the user and identifies the plugin in the URL — uses the
  // marketplace's installName (= manifestName post #176).
  const installKey = plugin.installName ?? legacyInstallKey(plugin);
  // proxyKey is the legacy-format install key that github-proxy's parseInstallKey
  // decodes into source/org/repo for download/delete operations.
  const proxyKey = legacyInstallKey(plugin);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [downloadingSkill, setDownloadingSkill] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<{ skill: string; message: string } | null>(null);
  const authenticated = isAuthenticated();

  useEffect(() => {
    setDownloadingSkill(null);
    setDownloadError(null);
  }, [installKey]);

  const isContributor =
    !!user?.email &&
    !!plugin.contributorEmail &&
    plugin.contributorEmail.toLowerCase() === user.email.toLowerCase();

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this? This cannot be undone.")) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteSkill(proxyKey);
      setDeleting(false);
      onDelete?.();
    } catch (err) {
      setDeleteError(err instanceof GitHubProxyError ? err.message : "Delete failed");
      setDeleting(false);
    }
  }

  const sectionHeading: React.CSSProperties = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#aaa",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    marginBottom: "0.5rem",
  };

  // Hooks and sub-agents are the only components that don't run in Claude Chat —
  // they need the full plugin runtime (Claude Cowork and Claude Code). Key the
  // caveat on the hooks/agents component arrays the page already renders. This
  // mirrors the catalog's supportedClients chat-exclusion rule (which also keys
  // on hooks/sub-agents) but does not read from it, so the two could drift if
  // that upstream rule ever changes.
  const coworkOnlyFeatures = [
    (plugin.hooks?.length ?? 0) > 0 ? "hooks" : null,
    (plugin.agents?.length ?? 0) > 0 ? "sub-agents" : null,
  ].filter((f): f is string => f !== null);
  const coworkOnlyFeatureText =
    coworkOnlyFeatures.length === 2
      ? `${coworkOnlyFeatures[0]} and ${coworkOnlyFeatures[1]}`
      : coworkOnlyFeatures[0];


  return (
    <div>
      <nav
        aria-label="Breadcrumb"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.25rem",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          padding: "0.5rem 0.75rem",
          marginBottom: "1.5rem",
          fontSize: "0.85rem",
        }}
      >
        <button
          onClick={onBackToCatalog}
          style={{ background: "none", border: "none", color: "#0078d4", cursor: "pointer", padding: 0, fontSize: "inherit" }}
        >
          Marketplace
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.installName ?? i} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
            <span style={{ color: "#9ca3af" }}>›</span>
            <button
              onClick={() => onBreadcrumbClick?.(crumb, i)}
              style={{ background: "none", border: "none", color: "#0078d4", cursor: "pointer", padding: 0, fontSize: "inherit" }}
            >
              {crumb.pluginName}
            </button>
          </span>
        ))}
        <span style={{ color: "#9ca3af" }}>›</span>
        <strong style={{ color: "#111827" }}>{plugin.pluginName}</strong>
      </nav>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "2.5rem", alignItems: "start" }}>

        <div style={{ minWidth: 0 }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <h1 style={{ margin: 0, fontSize: "1.5rem" }}>{plugin.pluginName}</h1>
              <OfficialBadge plugin={plugin} />
              {plugin.extensionType && (
                <span style={{
                  display: "inline-block",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  padding: "0.15rem 0.5rem",
                  borderRadius: "9999px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  color: plugin.extensionType === "plugin" ? "#7c3aed" : "#c2410c",
                  backgroundColor: plugin.extensionType === "plugin" ? "#f3e8ff" : "#fff7ed",
                }}>
                  {plugin.extensionType}
                </span>
              )}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#999", lineHeight: "1.4" }}>
              <div>{plugin.orgOrOwner}/{plugin.repoName}</div>
              <div>{plugin.source === "ghec" ? "GHEC" : "GHES"}</div>
            </div>
          </div>

          <p style={{ fontSize: "1.05rem", lineHeight: "1.6", color: "#444", marginBottom: "1.5rem" }}>
            {plugin.description}
          </p>

          {plugin.contributorName && (
            <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "1.5rem" }}>
              Contributed by <strong>{plugin.contributorName}</strong> on{" "}
              {new Date(plugin.firstDiscoveredAt).toLocaleDateString()}
            </p>
          )}

          {isContributor && (
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem" }}>
              <button
                type="button"
                onClick={onEdit}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.9rem",
                  backgroundColor: "#0078d4",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.9rem",
                  backgroundColor: "white",
                  color: "#cc0000",
                  border: "1px solid #cc0000",
                  borderRadius: "4px",
                  cursor: deleting ? "not-allowed" : "pointer",
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
              {deleteError && (
                <span style={{ fontSize: "0.85rem", color: "#cc0000", alignSelf: "center" }}>
                  {deleteError}
                </span>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.3rem 1rem", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
            <span style={{ color: "#aaa", fontWeight: 600 }}>Version</span>
            <span style={{ color: "#444" }}>{plugin.version}</span>

            {plugin.author && <>
              <span style={{ color: "#aaa", fontWeight: 600 }}>Author</span>
              <span style={{ color: "#444" }}>{plugin.author}</span>
            </>}

            {plugin.license && <>
              <span style={{ color: "#aaa", fontWeight: 600 }}>License</span>
              <span style={{ color: "#444" }}>{plugin.license}</span>
            </>}

            <span style={{ color: "#aaa", fontWeight: 600 }}>Updated</span>
            <span style={{ color: "#444" }}>{new Date(plugin.lastUpdatedAt).toLocaleDateString()}</span>

            {parentPlugin && <>
              <span style={{ color: "#aaa", fontWeight: 600 }}>Plugin</span>
              <span>
                <button
                  type="button"
                  // Navigate UP to the parent like its breadcrumb crumb does — not
                  // onNavigateToPlugin, which is forward navigation and would push this
                  // skill as the parent's ancestor. Pass the parent's real index in the
                  // trail so ancestors above it are preserved (e.g. grandparent → plugin
                  // → skill). On a direct visit breadcrumbs is empty → findIndex returns
                  // -1 → fallback 0, clearing the data-resolved single-level fallback.
                  onClick={() => {
                    const idx = breadcrumbs.findIndex((b) => b.installName === parentPlugin.installName);
                    onBreadcrumbClick?.(parentPlugin, idx >= 0 ? idx : 0);
                  }}
                  style={{ background: "none", border: "none", color: "#0078d4", cursor: "pointer", padding: 0, fontSize: "inherit", textAlign: "left", wordBreak: "break-word" }}
                >
                  {parentPlugin.pluginName} &rarr;
                </button>
              </span>
            </>}

            <span style={{ color: "#aaa", fontWeight: 600 }}>Repository</span>
            <span>
              {isSafeUrl(plugin.repoUrl) ? (
                <a href={plugin.repoUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#0078d4", textDecoration: "none", wordBreak: "break-all" }}>
                  {plugin.repoUrl} &rarr;
                </a>
              ) : (
                <span style={{ color: "#444" }}>{plugin.repoUrl}</span>
              )}
            </span>

            {plugin.homepage && plugin.homepage !== plugin.repoUrl && isSafeUrl(plugin.homepage) && <>
              <span style={{ color: "#aaa", fontWeight: 600 }}>Homepage</span>
              <span>
                <a href={plugin.homepage} target="_blank" rel="noopener noreferrer" style={{ color: "#0078d4", textDecoration: "none", wordBreak: "break-all" }}>
                  {plugin.homepage} &rarr;
                </a>
              </span>
            </>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "2rem" }}>
            {plugin.categories && plugin.categories.length > 0 && (
              <div>
                <div style={sectionHeading}>Category</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {plugin.categories.map((category) =>
                    onCategoryClick ? (
                      <button
                        key={category}
                        type="button"
                        onClick={() => onCategoryClick(category)}
                        className={styles.chipClickable}
                      >
                        {category}
                      </button>
                    ) : (
                      <span key={category} className={styles.chip}>
                        {category}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}

            {plugin.supportedClients && plugin.supportedClients.length > 0 && (
              <div>
                <div style={sectionHeading}>Works with</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {plugin.supportedClients.map((client) => {
                    const cfg = CLIENT_CONFIG[client] ?? { label: client };
                    return onClientClick ? (
                      <button
                        key={client}
                        type="button"
                        onClick={() => onClientClick(client)}
                        className={styles.chipClickable}
                      >
                        {cfg.label}
                      </button>
                    ) : (
                      <span key={client} className={styles.chip}>
                        {cfg.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {coworkOnlyFeatures.length > 0 && (
              <div
                role="note"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  backgroundColor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "0.6rem 0.75rem",
                  fontSize: "0.82rem",
                  lineHeight: "1.5",
                  color: "#1e3a5f",
                }}
              >
                <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.15rem" }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span>
                  This plugin&rsquo;s <strong>{coworkOnlyFeatureText}</strong> run in Claude Cowork
                  and Claude Code only — not in Claude Chat. Everything else in this plugin runs
                  in Claude Chat too.
                </span>
              </div>
            )}

            {plugin.keywords && plugin.keywords.length > 0 && (
              <div>
                <div style={sectionHeading}>Keywords</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {plugin.keywords.map((keyword) =>
                    onKeywordClick ? (
                      <button
                        key={keyword}
                        type="button"
                        onClick={() => onKeywordClick(keyword)}
                        className={styles.chipClickable}
                      >
                        {keyword}
                      </button>
                    ) : (
                      <span key={keyword} className={styles.chip}>
                        {keyword}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {plugin.extensionType === "plugin" && plugin.skills && plugin.skills.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <div style={{ ...sectionHeading, fontSize: "0.95rem", color: "#111", textTransform: "none", letterSpacing: 0, marginBottom: "0.75rem" }}>
                Skills included · {plugin.skills.length}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {plugin.skills.map((skill) => {
                  const linkedEntry = skill.installName ? findPlugin?.(skill.installName) : undefined;
                  // Defend against any skill whose installName resolves back to this same
                  // plugin (e.g. a single skill sharing the plugin's name) — render it
                  // inline rather than as a self-referential link that navigates to the
                  // same page and just stacks breadcrumbs. Catches already-published
                  // catalogs predating the catalog-builder fix that dropped these
                  // installNames (e.g. mythosaur), plus any future regression.
                  const isSelfReference =
                    !!linkedEntry &&
                    !!plugin.installName &&
                    linkedEntry.installName === plugin.installName;
                  const isLinked = !!linkedEntry && !isSelfReference;
                  const canDownload = !isLinked && authenticated && !!env.REACT_APP_GITHUB_PROXY_URL;
                  const isDownloading = downloadingSkill === skill.name;

                  async function handleSkillDownload() {
                    setDownloadingSkill(skill.name);
                    setDownloadError(null);
                    try {
                      const url = await getPresignedDownloadUrl(proxyKey, undefined, skill.name);
                      window.location.href = url;
                    } catch (err) {
                      setDownloadError({ skill: skill.name, message: err instanceof Error ? err.message : "Download failed" });
                    } finally {
                      // Guard against clearing a newer download's indicator when two
                      // skill downloads are started in quick succession.
                      setDownloadingSkill((prev) => prev === skill.name ? null : prev);
                    }
                  }

                  const skillIcon = (
                    <div style={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: isLinked ? "#eff6ff" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isLinked ? "#0078d4" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    </div>
                  );

                  if (isLinked) {
                    return (
                      <button
                        key={skill.name}
                        type="button"
                        onClick={() => linkedEntry && onNavigateToPlugin?.(linkedEntry)}
                        className={styles.skillCardLinked}
                      >
                        {skillIcon}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <span style={{ fontWeight: 600, fontSize: "1rem", color: "#0078d4" }}>{skill.name}</span>
                            <svg aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0078d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </div>
                          {skill.description && (
                            <div style={{ fontSize: "0.85rem", color: "#6b7280", lineHeight: "1.4", marginTop: "0.15rem" }}>
                              {skill.description}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  }

                  if (canDownload) {
                    return (
                      <div key={skill.name}>
                        <button
                          type="button"
                          onClick={handleSkillDownload}
                          disabled={isDownloading}
                          aria-label={`Download ${skill.name}`}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "0.85rem 1rem",
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: isDownloading ? "#f9fafb" : "white",
                            cursor: isDownloading ? "wait" : "pointer",
                            opacity: isDownloading ? 0.7 : 1,
                            textAlign: "left",
                            width: "100%",
                            font: "inherit",
                            gap: "0.85rem",
                          }}
                        >
                          {skillIcon}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontWeight: 600, fontSize: "1rem", color: "#111827" }}>{skill.name}</span>
                              <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </div>
                            {skill.description && (
                              <div style={{ fontSize: "0.85rem", color: "#6b7280", lineHeight: "1.4", marginTop: "0.15rem" }}>
                                {skill.description}
                              </div>
                            )}
                          </div>
                        </button>
                        {downloadError?.skill === skill.name && (
                          <div style={{ fontSize: "0.8rem", color: "#cc0000", marginTop: "0.25rem", paddingLeft: "0.25rem" }}>
                            {downloadError.message}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={skill.name}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        padding: "0.85rem 1rem",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: "0.85rem",
                      }}
                    >
                      {skillIcon}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: "1rem", color: "#111827" }}>{skill.name}</span>
                        {skill.description && (
                          <div style={{ fontSize: "0.85rem", color: "#6b7280", lineHeight: "1.4", marginTop: "0.15rem" }}>
                            {skill.description}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Plugin component sections ───────────────────────── */}
          {[
            { key: "mcpServers",   label: "MCP Servers",   iconColor: "#3b82f6", iconBg: "#eff6ff",  items: plugin.mcpServers?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "hooks",        label: "Hooks",          iconColor: "#d97706", iconBg: "#fef3c7",  items: plugin.hooks?.map(s => ({ primary: s.event, secondary: s.description })) },
            { key: "agents",       label: "Agents",         iconColor: "#9333ea", iconBg: "#f3e8ff",  items: plugin.agents?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "scripts",      label: "Scripts",        iconColor: "#059669", iconBg: "#ecfdf5",  items: plugin.scripts?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "monitors",     label: "Monitors",       iconColor: "#dc2626", iconBg: "#fef2f2",  items: plugin.monitors?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "outputStyles", label: "Output Styles",  iconColor: "#16a34a", iconBg: "#f0fdf4",  items: plugin.outputStyles?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "themes",       label: "Themes",         iconColor: "#c026d3", iconBg: "#fdf4ff",  items: plugin.themes?.map(s => ({ primary: s.name, secondary: s.description })) },
          ].filter(({ items }) => items && items.length > 0).map(({ key, label, iconColor, iconBg, items }) => (
            <div key={key} style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>
                {label} · {items!.length}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {items!.map(({ primary, secondary }) => (
                  <div
                    key={primary}
                    style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "0.75rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem", backgroundColor: "white" }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#111827" }}>{primary}</div>
                      {secondary && <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: "0.1rem", lineHeight: "1.4" }}>{secondary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {plugin.settings && plugin.settings.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>
                Settings · {plugin.settings.length}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", color: "#6b7280", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Setting</th>
                    <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", color: "#6b7280", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Description</th>
                    <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", color: "#6b7280", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Default</th>
                  </tr>
                </thead>
                <tbody>
                  {plugin.settings.map((s) => (
                    <tr key={s.name} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "0.6rem 0.75rem", fontFamily: "monospace", color: "#111827" }}>{s.name}</td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#374151" }}>{s.description}</td>
                      <td style={{ padding: "0.6rem 0.75rem", fontFamily: "monospace", color: "#6b7280" }}>{s.default ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {plugin.dependencies && plugin.dependencies.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>
                Dependencies · {plugin.dependencies.length}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                {plugin.dependencies.map((dep) => (
                  <span key={dep} style={{ backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "0.25rem 0.65rem", fontSize: "0.85rem", fontFamily: "monospace" }}>
                    {dep}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Skill component sections ────────────────────────── */}
          {plugin.triggerPhrases && plugin.triggerPhrases.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>Trigger Phrases</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                {plugin.triggerPhrases.map((phrase) => (
                  <span key={phrase} style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", borderRadius: "9999px", padding: "0.25rem 0.75rem", fontSize: "0.85rem" }}>
                    "{phrase}"
                  </span>
                ))}
              </div>
            </div>
          )}

          {plugin.examplePrompts && plugin.examplePrompts.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>Example Prompts</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {plugin.examplePrompts.map((prompt) => (
                  <div key={prompt} style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "0.75rem 1rem", display: "flex", alignItems: "flex-start", gap: "0.75rem", backgroundColor: "white" }}>
                    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.1rem" }}>
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span style={{ fontSize: "0.9rem", color: "#374151", fontStyle: "italic", lineHeight: "1.5" }}>{prompt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plugin.requiredContext && plugin.requiredContext.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>Required Context</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {plugin.requiredContext.map((req) => (
                  <div key={req} style={{ backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "0.65rem 0.85rem", display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
                    <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.1rem" }}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span style={{ fontSize: "0.85rem", color: "#92400e", lineHeight: "1.5" }}>{req}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const resolvedRelated = plugin.relatedSkills
              ?.map((installName) => ({ installName, entry: findPlugin?.(installName) }))
              .filter((p): p is { installName: string; entry: MarketplaceEntry } => !!p.entry);
            if (!resolvedRelated?.length) return null;
            return (
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#111", marginBottom: "0.75rem" }}>Related Plug-ins and Skills</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {resolvedRelated.map(({ installName, entry }) => (
                    <button
                      key={installName}
                      type="button"
                      onClick={() => onNavigateToPlugin?.(entry)}
                      className={styles.relatedChip}
                    >
                      {entry.pluginName} →
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>

        <aside style={{ position: "sticky", top: "2rem" }}>
          <InstallInClaude plugin={plugin} />
          {plugin.extensionType === "plugin" && (
            <details>
              <summary style={{ fontSize: "0.85rem", color: "#0078d4", cursor: "pointer", fontWeight: 600, marginBottom: "0.75rem" }}>
                CLI install commands
              </summary>
              <InstallInstructions plugin={plugin} />
            </details>
          )}
          <DesktopDownload
            key={installKey}
            plugin={plugin}
          />
          {onHelp && (
            <button
              onClick={onHelp}
              style={{
                marginTop: "1.25rem",
                padding: 0,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "0.8rem",
                color: "#0078d4",
              }}
            >
              Full setup guide &rarr;
            </button>
          )}
        </aside>

      </div>
    </div>
  );
}
